import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BaseCondition,
  ConditionValidationState,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

import {getConditionNameSuffix} from './conditionArchiveDisplayUtils';
import {DEFAULT_CONDITION_VALIDATION_STATE} from './conditionUtils';
import {getConditionArchiveState, isFullyPendingRemoved} from './replicateTableUtils';

/** One table row: persisted values plus optional display overrides. */
export interface TransposedReplicateTableRow {
  realIndex: number;
  name: string;
  nameDisplay?: string;
  /** Editable count, excluding archived replicates. */
  numReplicates: number;
  /** Displayed count, including archived replicates when they are visible. */
  displayedReplicateCount: number;
  description: string;
  validationState: ConditionValidationState;
  locked: boolean;
}

export interface ArchivedDisplayOptions {
  showArchived: boolean;
  /** Fully pending-removed replicate lane count keyed by condition index. */
  pendingRemovedByConditionIndex?: ReadonlyMap<number, number>;
}

/** Rows already derived for a parent summary, so the table does not derive them again. */
export interface PrecomputedTransposedReplicateRows {
  rows: Array<TransposedReplicateTableRow>;
  hiddenConditionIndices: Set<number>;
}

/** Row summary, including archive information, used to summarize or to fill a table row. */
interface TransposedReplicateSummaryRow {
  realIndex: number;
  /** Displayed count, including archived replicates when they are visible. */
  displayedReplicateCount: number;
  isFullyArchived: boolean;
  isFullyPending: boolean;
}

interface TransposedReplicateSummaryRows {
  rows: Array<TransposedReplicateSummaryRow>;
  hiddenConditionIndices: Set<number>;
}

interface BuildTransposedReplicateTableRowsOptions {
  isPlan: boolean;
  archivedDisplay?: ArchivedDisplayOptions;
}

function buildTransposedReplicateSummaryRows<TCondition extends BaseCondition>(
  conditions: Array<TCondition>,
  archivedDisplay?: ArchivedDisplayOptions
): TransposedReplicateSummaryRows {
  const isArchiveDisplayEnabled = archivedDisplay != null;
  const showArchived = archivedDisplay?.showArchived ?? false;
  const pendingRemovedByConditionIndex = archivedDisplay?.pendingRemovedByConditionIndex;
  const rows: Array<TransposedReplicateSummaryRow> = [];
  const hiddenConditionIndices = new Set<number>();

  conditions.forEach((condition, realIndex) => {
    const {isFullyArchived, liveReplicateCount, archivedReplicateCount} = getConditionArchiveState(
      condition,
      isArchiveDisplayEnabled
    );
    const pendingRemovedReplicateCount = pendingRemovedByConditionIndex?.get(realIndex) ?? 0;
    const remainingLive = Math.max(0, liveReplicateCount - pendingRemovedReplicateCount);
    const isFullyPending = isFullyPendingRemoved(liveReplicateCount, pendingRemovedReplicateCount);
    const shouldHideWhenArchivedOff = isArchiveDisplayEnabled && (isFullyArchived || isFullyPending);
    if (shouldHideWhenArchivedOff && !showArchived) {
      hiddenConditionIndices.add(realIndex);
      return;
    }
    rows.push({
      realIndex,
      displayedReplicateCount: showArchived ? liveReplicateCount + archivedReplicateCount : remainingLive,
      isFullyArchived,
      isFullyPending,
    });
  });

  return {rows, hiddenConditionIndices};
}

/** Builds table rows from each summary row, adding table-only fields such as the name. */
export function buildTransposedReplicateTableRows<TCondition extends BaseCondition>(
  conditions: Array<TCondition>,
  {isPlan, archivedDisplay}: BuildTransposedReplicateTableRowsOptions
): PrecomputedTransposedReplicateRows {
  const {rows: summaryRows, hiddenConditionIndices} = buildTransposedReplicateSummaryRows(
    conditions,
    archivedDisplay
  );
  const rows = summaryRows.map((summaryRow): TransposedReplicateTableRow => {
    const condition = conditions[summaryRow.realIndex];
    const nameDisplay = `${condition.name}${getConditionNameSuffix({
      isArchived: summaryRow.isFullyArchived,
      isPendingArchive: summaryRow.isFullyPending,
    })}`;
    return {
      realIndex: summaryRow.realIndex,
      name: condition.name,
      nameDisplay,
      numReplicates: condition.numReplicates,
      displayedReplicateCount: summaryRow.displayedReplicateCount,
      description: condition.description ?? '',
      validationState: condition.validationState ?? DEFAULT_CONDITION_VALIDATION_STATE,
      locked: isPlan ? false : isPersistedCondition(condition),
    };
  });

  return {rows, hiddenConditionIndices};
}

/** Summarizes the same rows a parent rendered, so a header cannot drift from the table. */
export function summarizeTransposedReplicateRows(
  rows: Array<{displayedReplicateCount: number}>,
  procedureType: ProcedureType
): string {
  const totalReplicates = rows.reduce((acc, row) => acc + row.displayedReplicateCount, 0);
  const conditionWord = getProcedureRunConditionTextForProcedureType(procedureType);
  return `${rows.length} ${pluralize(rows.length, conditionWord)}, ${totalReplicates} ${pluralize(
    totalReplicates,
    'replicate'
  )}`;
}

/** Summarizes visible conditions, including archive information, without table-only fields. */
export function summarizeConditionGroups<TCondition extends BaseCondition>(
  conditions: Array<TCondition>,
  procedureType: ProcedureType,
  archivedDisplay?: ArchivedDisplayOptions
): string {
  const {rows} = buildTransposedReplicateSummaryRows(conditions, archivedDisplay);
  return summarizeTransposedReplicateRows(rows, procedureType);
}
